import { Invoice } from "../interfaces/invoice";
import { fetchWithAuth } from "./fetchWithAuth";
import { convertBase64ToBlob, convertBlobToBase64 } from "./helpers";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getInvoices = async (): Promise<Invoice[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/Invoice/GetInvoices`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch invoices");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};

export const getIndividualInvoices = async (): Promise<Invoice[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/Invoice/GetIndividualInvoices`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch invoices");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};

export const generateInvoiceReport = async (
  invoices: Invoice[],
): Promise<Blob | null> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Invoice/GenerateInvoiceReport`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(invoices),
      },
    );

    if (!response.ok) {
      console.error("Failed to generate the invoice report.", response);
      return null;
    }

    const result = await response.json();

    if (result.success && result.csvBase64) {
      // Convert Base64 string to Blob
      const csvBlob = await convertBase64ToBlob(result.csvBase64, "text/csv");
      return csvBlob;
    } else {
      console.error("Error from server:", result.message);
      return null;
    }
  } catch (error) {
    console.error("Error generating the invoice report:", error);
    return null;
  }
};

export const saveInvoiceReport = async (
  invoices: Invoice[],
  pdfBlob: Blob,
): Promise<boolean> => {
  try {
    const finalReportBase64 = await convertBlobToBase64(pdfBlob);
    const formData = {
      invoices,
      finalReportBase64,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Invoice/SaveInvoiceReport`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save invoice report.");
      return false;
    }

    const result = await response.json();

    if (result.success) {
      console.log("Invoice report saved successfully.");
      return true;
    } else {
      console.error("Error from server:", result.message);
      return false;
    }
  } catch (error) {
    console.error("Error saving the invoice report:", error);
    return false;
  }
};

export const generateRevenueReport = async (
  invoices: Invoice[],
): Promise<Blob | null> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Invoice/GenerateRevenueReport`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(invoices),
      },
    );

    if (!response.ok) {
      console.error("Failed to generate the revenue report.", response);
      return null;
    }

    const result = await response.json();

    if (result.success && result.pdfBase64) {
      // Convert Base64 to Blob URL
      const byteCharacters = atob(result.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      return blob;
    } else {
      console.error("Error from server:", result.message);
      return null;
    }
  } catch (error) {
    console.error("Error generating the revenue report:", error);
    return null;
  }
};
